import {
  ApolloClient,
  ApolloError,
  HttpLink,
  InMemoryCache,
  type ServerError,
} from "@apollo/client";
import { SHOPIFY_API_VERSION } from "@intelligems/ig-types/functions/shopify/api-version";
import { shopifyStorefrontAuthHeader } from "@intelligems/ig-utils/storefront-token-auth";

import {
  StorefrontChannelLockedError,
  type StorefrontClient,
  type StorefrontCredential,
} from "@src/features/experienceBuilder/toolbar/Navigator/storefront/StorefrontClient";

const credentialHeaders = (
  credential: StorefrontCredential
): Record<string, string> =>
  credential.kind === "storefrontToken"
    ? shopifyStorefrontAuthHeader(credential.token)
    : {};

export const storefrontEndpoint = (shop: string) =>
  `https://${shop}/api/${SHOPIFY_API_VERSION}/graphql.json`;

/**
 * The message of a tokenless request to a store with a password. Shopify
 * answers HTTP 400 with
 * `{"errors":[{"message":"Online Store channel is locked.","extensions":{"code":"BAD_REQUEST"}}]}`
 * (tested on API 2026-04). `BAD_REQUEST` is used for other errors too, so the
 * message is the only thing that identifies the lock.
 */
const CHANNEL_LOCKED_MESSAGE = "Online Store channel is locked.";

const isServerError = (
  error: ApolloError["networkError"]
): error is ServerError => error?.name === "ServerError";

/**
 * Apollo rejects a non-2xx response with a `ServerError` network error that
 * holds the parsed body in `result`.
 */
const isChannelLocked = (error: unknown) => {
  if (!(error instanceof ApolloError)) {
    return false;
  }
  const { networkError } = error;
  if (!isServerError(networkError) || networkError.statusCode !== 400) {
    return false;
  }
  const { result } = networkError;
  const errors: unknown =
    typeof result === "object" && "errors" in result ? result.errors : [];
  return (
    Array.isArray(errors) &&
    errors.some(
      (graphQLError: unknown) =>
        typeof graphQLError === "object" &&
        graphQLError !== null &&
        "message" in graphQLError &&
        graphQLError.message === CHANNEL_LOCKED_MESSAGE
    )
  );
};

/**
 * A Storefront client with one Apollo client for every previewed store. The
 * Storefront API allows any origin (`access-control-allow-origin: *`), so the
 * browser calls `https://{shop}/api/{SHOPIFY_API_VERSION}/graphql.json`
 * directly. Each request sets its own URI and credential through the Apollo
 * context. `fetch` sends the HTTP requests, so tests give a fake.
 */
export const createApolloStorefrontClient = (
  fetch: typeof globalThis.fetch
): StorefrontClient => {
  const apollo = new ApolloClient({
    link: new HttpLink({ fetch }),
    cache: new InMemoryCache(),
  });

  return {
    query: async ({ shop, credential, document, variables }) => {
      try {
        const result = await apollo.query({
          query: document,
          variables,
          fetchPolicy: "no-cache",
          context: {
            uri: storefrontEndpoint(shop),
            headers: credentialHeaders(credential),
          },
        });
        return result.data;
      } catch (error) {
        if (isChannelLocked(error)) {
          throw new StorefrontChannelLockedError(shop);
        }
        throw error;
      }
    },
  };
};

/** The real Storefront client. `NavigatorProvider` gives it to the Navigator. */
export const apolloStorefrontClient = createApolloStorefrontClient(
  (input, init) => fetch(input, init)
);
